package nl.volleyplanner.web.controller.thuisprogramma

import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.launch
import kotlinx.coroutines.withContext
import kotlinx.html.*
import kotlinx.html.js.onClickFunction
import kotlinx.html.js.span
import nl.volleyplanner.api.entities.Group
import nl.volleyplanner.api.planner.APITaskStatus
import nl.volleyplanner.api.planner.PlanningProblem
import nl.volleyplanner.api.planner.ProblemStatistics
import nl.volleyplanner.api.planner.Task
import nl.volleyplanner.api.sports.Match
import nl.volleyplanner.web.ApiDataStateFlow
import nl.volleyplanner.web.CoroutineManager
import nl.volleyplanner.web.VolleyPlannerApplication
import nl.volleyplanner.web.base.HTMLComponent
import nl.volleyplanner.web.base.component
import nl.volleyplanner.web.base.component.ListeningComponent
import nl.volleyplanner.web.base.component.badge
import nl.volleyplanner.web.base.simpleComponent
import nl.volleyplanner.web.base.spinner
import nl.volleyplanner.web.component.TaskAssignmentStatisticsScoreBadge
import nl.volleyplanner.web.controller.thuisprogramma.assignee_finder.AssigneeFinder2
import nl.volleyplanner.web.controller.thuisprogramma.assignee_finder.TaskAssigneeStore
import nl.volleyplanner.web.launchAsync
import nl.volleyplanner.web.lib.Tooltip
import nl.volleyplanner.web.textconverters.TaskLabelConverter
import nl.volleyplanner.web.util.BootstrapColor
import nl.volleyplanner.web.util.combineAndLaunch
import org.w3c.dom.HTMLElement
import org.w3c.dom.get

/**
 * Component to display a single taskassignment
 *
 * Displays the name, along with an option to edit the assignment.
 * Editting the assignment opens an AssigneeFinder to help find an alternative
 */
class TaskAssigneeDisplay(
    private val problem: PlanningProblem,
    private val match: Match,
    private val task: Task?,
    private val thuisprogrammaDataManager: ThuisprogrammaDataManager,
    private val thuisprogrammaFilters: ThuisprogrammaFilters,
    private val taskAssigneeStore: TaskAssigneeStore?,
    private val teamToHighlight: Group?
) : HTMLComponent() {
    companion object {
        private var currentAssigneeFinder: AssigneeFinder2? = null
    }

    private var assigneeFinder: AssigneeFinder2? = null
    private val statisticsState: ApiDataStateFlow<ProblemStatistics, *>
        get() = thuisprogrammaDataManager.problemData.getSelectedVersionData().thuisprogrammaStatisticsState
    private val thuisprogrammaControls: ThuisprogrammaControls
        get() = thuisprogrammaDataManager.thuisprogrammaControlsState.value
    private val changesBehindByTaskId: Map<String, Task>?
        get() = thuisprogrammaDataManager.problemData.getSelectedVersionData().changesBehindPublishedByTaskIdState.value

    data class RenderModel(
        val task: Task?,
        val thuisprogrammaControls: ThuisprogrammaControls,
        val canPublishVersion: Boolean,
        val changesBehindByTaskId: Map<String, Task>?
    )

    // TODO: Create a single assignee finder, enable it to change state to a specific task
    //  and provide that context from here when displaying it
    private suspend fun getAssigneeFinder(): AssigneeFinder2 {
        withContext(Dispatchers.Main) {
            if (assigneeFinder == null) {
                assigneeFinder = AssigneeFinder2(problem, thuisprogrammaDataManager.problemData.selectedProblemVersionSlugState.value, match, taskAssigneeStore!!, thuisprogrammaDataManager)
            }
            if (assigneeFinder!!.isDisposed()) {
                createStandaloneComponent(assigneeFinder!!)
            }
        }
        return assigneeFinder!!
    }

    override suspend fun getState(): StateFlow<*> = listOf(
        taskAssigneeStore?.state ?: MutableStateFlow(null),
        thuisprogrammaDataManager.thuisprogrammaControlsState,
        thuisprogrammaDataManager.problemData.getSelectedVersionData().canPublishVersion,
        thuisprogrammaDataManager.problemData.getSelectedVersionData().changesBehindPublishedByTaskIdState.subscribe()
    ).combineAndLaunch(CoroutineManager.RENDER_SCOPE)

    override fun render(consumer: TagConsumer<HTMLElement>): HTMLElement {
        val renderModel = RenderModel(
            taskAssigneeStore?.state?.value,
            thuisprogrammaDataManager.thuisprogrammaControlsState.value,
            thuisprogrammaDataManager.problemData.getSelectedVersionData().canPublishVersion.value,
            thuisprogrammaDataManager.problemData.getSelectedVersionData().changesBehindPublishedByTaskIdState.value
        )
        return consumer.span {
            classes += "tp-assignee-display"
            if (renderModel.thuisprogrammaControls.editMode && renderModel.canPublishVersion) {
                if (renderModel.task != null) {
                    val lockButtonClass = when (renderModel.task.locked) {
                        true -> "btn-dark"
                        false -> "btn-light"
                    }
                    button(classes = "btn btn-sm me-2 d-print-none") {
                        classes += lockButtonClass
                        style =
                            "--bs-btn-padding-y: .1rem; --bs-btn-padding-x: .2rem; --bs-btn-font-size: .75rem; margin-right: 0.1em;"
                        onClickFunction = { event ->
                            launchAsync {
                                VolleyPlannerApplication.volleyplannerApiClient
                                    .organisation
                                    .plannerConfiguration
                                    // TODO: Listen for actual version
                                    .getTasksEndpoint(problem.slug, thuisprogrammaDataManager.problemData.selectedProblemVersionSlugState.value)
                                    .setTaskLock(renderModel.task, !renderModel.task.locked)
                            }.onComplete {
                                taskAssigneeStore?.updateTask(renderModel.task.copy(locked = !renderModel.task.locked))
                            }
                        }

                        val buttonIconClass = when (renderModel.task.locked) {
                            true -> "bi-lock-fill"
                            false -> "bi-unlock-fill"
                        }
                        i(classes = "bi $buttonIconClass") {}
                        span(classes = "visually-hidden") {}
                    }
                    val buttonClass = when (renderModel.task.assignee.personAssignee) {
                        null -> "btn-danger"
                        else -> "btn-secondary"
                    }
                    button(classes = "btn btn-sm me-2 d-print-none") {
                        classes += buttonClass
                        style =
                            "--bs-btn-padding-y: .1rem; --bs-btn-padding-x: .2rem; --bs-btn-font-size: .75rem; margin-right: 0.1em;"
                        onClickFunction = { event ->
                            CoroutineScope(Dispatchers.Main).launch {
                                currentAssigneeFinder?.hide()
                                currentAssigneeFinder = getAssigneeFinder()
                                currentAssigneeFinder!!.refreshData()
                                currentAssigneeFinder!!.show()
                            }
                        }

                        i(classes = "bi bi-pencil-square") {}
                        span(classes = "visually-hidden") {}
                    }
                }
            }


            if (renderModel.thuisprogrammaControls.showChangesWithOtherVersion) {
                val otherVersionStoresState = thuisprogrammaDataManager.problemData
                    .getVersionData(renderModel.thuisprogrammaControls.showChangesToVersionSlug)
                    .assigneeStoresState
                component(ListeningComponent("AssigneeDisplayChangesWithOther", listOf(otherVersionStoresState)) {
                    val otherAssigneeStore = otherVersionStoresState.value?.get(TaskAssigneeStore.getKey(match, task!!))
                    if (otherAssigneeStore != null && otherAssigneeStore.task.assignee.personAssignee?.id != renderModel.task?.assignee?.personAssignee?.id) {
                        // TODO: Allow components to not return HTMLElements
                        span {
                            span(classes = "text-decoration-line-through") {
                                +TaskLabelConverter.assigneeNameUIString(otherAssigneeStore.task)
                            }
                            span(classes = "mx-1") {
                                +"→"
                            }
                        }
                    } else {
                        span {}
                    }
                })
            }
            span {
                if (task?.status == APITaskStatus.Cancelled) {
                    classes += "text-decoration-line-through"
                }
                if (teamToHighlight?.memberIds?.contains(renderModel.task?.assignee?.personAssignee?.id) == true) {
                    classes += "highlighted"
                }

                +TaskLabelConverter.assigneeNameUIString(renderModel.task)
            }
            if (thuisprogrammaDataManager.problemData.selectedProblemVersionSlug != null && renderModel.changesBehindByTaskId?.containsKey(task!!.id) == true) {
                val task = renderModel.changesBehindByTaskId.getValue(task.id)
                badge(color = BootstrapColor.Danger, small = true) {
                    // TODO: remove this from base function
                    classes = classes - setOf("d-inline-flex", "align-items-center")
                    classes += "text-start mt-1"
                    strong (classes = "text-black") { +"Geupdate in gepubliceerde versie" }
                    br {}
                    +"→ "
                    +TaskLabelConverter.assigneeNameUIString(task)
                }
            }
            if (renderModel.thuisprogrammaControls.statisticsMode && taskAssigneeStore != null) {
                component(ListeningComponent(
                    "AssigneeDisplayStatistics", listOf(statisticsState.subscribe()),
                    onContentReplacedFunction = { htmlElement ->
                        // Initialize tooltips
                        val tooltipElements = htmlElement.querySelectorAll("[data-bs-toggle=\"tooltip\"]")
                        for (i in 0 until tooltipElements.length) {
                            val tooltipElement = tooltipElements[i] as HTMLElement
                            Tooltip.getOrCreateInstance(tooltipElement)
                        }
                    }
                ) {
                    div (classes = "ms-2 my-1") {
                        if (statisticsState.value == null) {
                            spinner(small = true)
                        } else if (renderModel.task != null) {
                            val taskStatistics = statisticsState.value!!.taskStatisticsById.getValue(renderModel.task.id)
                            val personStatistics = renderModel.task.assignee.personAssignee?.id?.let {
                                statisticsState.value!!.personStatisticsById[it]
                            }
                            simpleComponent(TaskAssignmentStatisticsScoreBadge(taskStatistics, personStatistics))
                        }
                    }
                })
            }
        }
    }
}